<template>
  <div>
    <v-dialog v-model="isConfirmUploadExtractModalOpen" :max-width="300">
      <BaseDialogCard
        :title="tkey('ftpFileUploadTitle')"
        @close="toggleIsConfirmSendModalOpen(false)"
      >
        <span>{{ dialogMessage }}</span>
        <template #footer>
          <v-row class="ma-0 mt-3">
            <v-col class="d-flex justify-end">
              <v-btn
                data-id-e2e="btnCancelUploadFtp"
                height="24"
                @click="toggleIsConfirmSendModalOpen(false)"
              >
                {{ $t('actions.cancel') }}
              </v-btn>
              <v-btn
                data-id-e2e="btnConfirmUploadFtp"
                height="24"
                class="ml-2"
                action
                @click="[uploadExtract(), toggleIsConfirmSendModalOpen(false)]"
              >
                {{ $t('actions.upload') }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </BaseDialogCard>
    </v-dialog>
    <!-- In this case will always have only one item -->
    <a
      v-if="isUploadVisible && !isMenuVisible"
      :disabled="isDisabled"
      @click.prevent="openFtpPopup(visibleItems[0])"
    >
      <v-tooltip location="bottom">
        <template #activator="{ props: tooltipActivatorProps }">
          <v-btn
            data-id-e2e="btnUploadExtracts"
            v-bind="{ ...tooltipActivatorProps }"
            icon
            variant="text"
            :loading="loading"
            :disabled="isDisabled"
          >
            <v-icon size="23">mdi-tray-arrow-up</v-icon>
          </v-btn>
        </template>
        <span>{{ $t('actions.upload') }}</span>
      </v-tooltip>
    </a>

    <v-menu
      v-else-if="isUploadVisible && isMenuVisible"
      :disabled="isDisabled"
      :z-index="menuZindex"
      offset="2"
    >
      <template #activator="{ props }">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipActivatorProps }">
            <v-btn
              data-id-e2e="btnUploadExtracts"
              v-bind="{ ...props, ...tooltipActivatorProps }"
              icon
              variant="text"
              :loading="loading"
              :disabled="isDisabled"
            >
              <v-icon size="23">mdi-tray-arrow-up</v-icon>
            </v-btn>
          </template>
          <span>{{ $t('actions.upload') }}</span>
        </v-tooltip>
      </template>
      <v-list>
        <v-list-item
          v-for="(item, index) in visibleItems"
          :key="index"
          :disabled="isUploadItemDisabled(item)"
          link
        >
          <v-list-item-title @click.prevent="openFtpPopup(item)">
            {{ item.title }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { ref } from 'vue';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { get, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import ftpUploadStatuses from '@enums/ftp-upload-statuses';
import extractTypes from '@enums/extract-types';
import ftpUploadTypes from '@enums/ftp-upload-types';
import menus from '@enums/menus';
import { useCompoundExtractStore } from '@/stores/compound-extract';
import { useFilesStore } from '@/stores/files';
import { useSnackbarStore } from '@/stores/snackbar';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useFeature } from '@/composables/use-feature';

const { AOC, INTEGRATION } = extractTypes;
const { finished, error } = ftpUploadStatuses;

dayjs.extend(utc);

export default {
  setup() {
    const { tkey } = useInternationalization('compoundExtractsPage.uploadButton');
    const { canEditCompoundExtracts, canManuallyPublishCompoundExtracts } = usePermission();
    const { ftpSpaceTestEnabled, publishToDataPlatform } = useFeature();
    const menuZindex = ref(menus.zIndexes.menu);

    return {
      tkey,
      canEditCompoundExtracts,
      canManuallyPublishCompoundExtracts,
      ftpSpaceTestEnabled,
      publishToDataPlatform,
      menuZindex,
    };
  },

  data() {
    return {
      loading: false,
      dialogMessage: this.tkey('ftpFileUploadConfirmation'),
      selectedFtpType: ftpUploadTypes.planningHub,
      dayjs,
      finished,
      error,
      isConfirmUploadExtractModalOpen: false,
    };
  },

  computed: {
    ...mapState(useCompoundExtractStore, ['getExtractById', 'extractSeriesIdBySettingId']),

    items() {
      return [
        {
          title: this.$t('compoundExtractsPage.planningHub'),
          type: ftpUploadTypes.planningHub,
          message: this.tkey('ftpFileUploadConfirmation'),
          showItem: !this.spaceTestOnly,
        },
        {
          title: this.$t('compoundExtractsPage.spaceTest'),
          type: ftpUploadTypes.spaceTest,
          message: this.tkey('ftpSpaceTestFileUploadConfirmation'),
          showItem: this.ftpSpaceTestEnabled,
        },
        {
          title: this.$t('compoundExtractsPage.retailDataPlatform'),
          type: ftpUploadTypes.retailDataPlatform,
          message: this.tkey('RetailDataPlatformFileUploadConfirmation'),
          showItem:
            this.publishToDataPlatform &&
            this.canManuallyPublishCompoundExtracts &&
            !this.spaceTestOnly,
        },
      ];
    },

    visibleItems() {
      return this.items.filter(item => item.showItem);
    },

    isMenuVisible() {
      return this.visibleItems.length > 1;
    },

    extractId() {
      return get(this.params, 'extractId');
    },

    extract() {
      return this.getExtractById(this.extractId) || {};
    },

    mostRecentOutbound() {
      return get(this.params, 'mostRecentOutbound', null);
    },

    extractGenerationJobStatus() {
      return get(this.extract, 'jobs.compoundExtract.status');
    },

    isExtractReadyForFtpUpload() {
      return this.extractGenerationJobStatus === this.finished && size(this.extract.blobId);
    },

    spaceTestOnly() {
      return get(this.params, 'spaceTestOnly', false);
    },

    isUploadVisible() {
      return this.extract.extractType === AOC;
    },

    isDisabled() {
      return this.visibleItems.every(item => this.isUploadItemDisabled(item));
    },
  },

  methods: {
    ...mapActions(useFilesStore, ['uploadFileToFtp']),
    ...mapActions(useSnackbarStore, ['showSuccess', 'showError']),
    ...mapActions(useCompoundExtractStore, ['generateCompoundExtract', 'updateExtract']),

    isUploadItemDisabled(item) {
      if (!this.canEditCompoundExtracts || !this.extract._id || !this.extract.extractSettingsId) {
        return true;
      }

      // Retail Data Platform publish is allowed even while the extract generation job is still running.
      if (item?.type === ftpUploadTypes.retailDataPlatform) {
        return false;
      }

      return !this.isExtractReadyForFtpUpload;
    },

    openFtpPopup(item) {
      if (this.isUploadItemDisabled(item)) return;
      this.dialogMessage = item.message;
      this.selectedFtpType = item.type;
      this.openPopup();
    },

    async handlePublishToDataPlatform({ isUploadedToPlanningHub }) {
      try {
        await this.generateCompoundExtract(
          this.extract,
          this.extract.extractSettingsId,
          INTEGRATION,
          isUploadedToPlanningHub
        );
        this.showSuccess(this.tkey('retailDataPlatformPublishSuccess'));
      } catch (err) {
        this.showError(this.tkey('retailDataPlatformPublishError'));
      }
    },

    openPopup() {
      if (this.isDisabled) return;
      this.toggleIsConfirmSendModalOpen(true);
    },

    async uploadExtract() {
      // Handles file upload or publishing to Retail Data Platform based on selected type.
      if (this.isUploadItemDisabled({ type: this.selectedFtpType })) return;
      this.loading = true;

      // Retail Data Platform publish workflow
      if (
        this.selectedFtpType === ftpUploadTypes.retailDataPlatform &&
        this.publishToDataPlatform
      ) {
        await this.handlePublishToDataPlatform({ isUploadedToPlanningHub: false });
        this.loading = false;
        await this.params.refresh();
        return;
      }

      // FTP upload workflow
      let status = finished;
      const { filenameOnDownload } = this.extract;
      let filename = filenameOnDownload;
      try {
        filename = await this.uploadFileToFtp({
          ftpType: this.selectedFtpType,
          ...this.extract,
        });
      } catch (err) {
        status = error;
        this.showError(this.tkey('ftpUploadError'));
        this.loading = false;
      } finally {
        const extractSeriesId = this.extractSeriesIdBySettingId[this.extract.extractSettingsId];
        await this.updateExtract({
          status,
          filename,
          ftpType: this.selectedFtpType,
          settings: {
            _id: this.extract.extractSettingsId,
            series: { _id: extractSeriesId },
          },
        });
        this.showSuccess(this.tkey('ftpUploadSuccess'));
        this.loading = false;
        await this.params.refresh();

        // As the last step, if it's a PH upload that was successful, publish extract to data platform
        if (
          status === finished &&
          this.selectedFtpType === ftpUploadTypes.planningHub &&
          this.publishToDataPlatform
        ) {
          // Kick off the workflow to publish the integration extract, don't wait for it to finish
          // automatically publish to data platform after integration extract is generated
          await this.handlePublishToDataPlatform({ isUploadedToPlanningHub: true });
        }
      }
    },

    toggleIsConfirmSendModalOpen(isOpen) {
      this.isConfirmUploadExtractModalOpen = isOpen;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

a {
  text-decoration: none;
}

.icon {
  margin: 0 8px !important;
}

.finished {
  color: $assortment-positive-action-colour;
}

.error-icon {
  color: $assortment-negative-action-colour;
}

:deep(.upload-extracts__button) {
  height: 29px !important;
  padding: 0 12px !important;

  > span {
    font-size: 1.4rem;
  }
}

.v-list-item {
  min-height: unset;
  height: 28px;
}
</style>
